import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, FormCheckbox, FormInput, FormSelect, TradingStrategySelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import { parsePositiveInt } from './entryOptimizerUtils';

/**
 * Variant study wizard (Phase 7): pick a strategy, edit the level-key domains per
 * axis (the grid builder), review the runnability matrix, pick the universe
 * source, then launch. Each runnable variant costs one real discovery run per
 * stage — the grid size is the study's price tag.
 */
const EntryOptimizerStudyNew = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [strategyId, setStrategyId] = useState<number | null>(parsePositiveInt(searchParams.get('strategyId')));
    const [sourceExperimentId, setSourceExperimentId] = useState('');
    // axisId -> comma/space separated level keys (edited by the user).
    const [domainDraft, setDomainDraft] = useState<Record<string, string>>({});
    const [pilotEntityCount, setPilotEntityCount] = useState('10');
    const [fullTopK, setFullTopK] = useState('3');
    const [includeGateDiscovery, setIncludeGateDiscovery] = useState(true);

    const { strategies } = useTradingStrategies();
    const strategy = useMemo(() => strategies.find((candidate) => candidate.id === strategyId) ?? null, [strategies, strategyId]);
    const sourceVersionId = strategy?.tradeLogicV2EntryVersionId ?? null;

    const domainOverrides = useMemo(() => {
        const overrides: Record<string, string[]> = {};

        for (const [axisId, draft] of Object.entries(domainDraft)) {
            const values = draft
                .split(/[\s,]+/)
                .map((value) => value.trim())
                .filter(Boolean);

            if (values.length) overrides[axisId] = values;
        }

        return overrides;
    }, [domainDraft]);

    const preflightQuery = useAppQuery({
        ...trpc.entryOptimizer.studyPreflight.queryOptions({
            sourceVersionId: sourceVersionId ?? 0,
            ...(Object.keys(domainOverrides).length ? { domainOverrides } : {})
        }),
        enabled: sourceVersionId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const preflight = preflightQuery.data?.preflight ?? null;
    const runnableVariants = useMemo(() => (preflight?.grid ?? []).filter((variant) => variant.runnable), [preflight]);
    const unavailableVariants = useMemo(() => (preflight?.grid ?? []).filter((variant) => !variant.runnable), [preflight]);

    const experimentsQuery = useAppQuery({
        ...trpc.experiment.list.queryOptions({ executionStatus: 'completed', limit: 100, offset: 0 }),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const experimentOptions = useMemo(
        () =>
            (experimentsQuery.data?.experiments ?? [])
                .filter((experiment) => experiment.strategyId === strategyId)
                .map((experiment) => ({
                    label: `#${experiment.id} ${experiment.name} (${experiment.entityCount} entities)`,
                    value: String(experiment.id)
                })),
        [experimentsQuery.data?.experiments, strategyId]
    );

    const createStudyMutation = useMutation({
        ...trpc.entryOptimizer.createStudy.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: (result) => {
            showToast(
                `Variation search started — testing ${result.created.runnableVariants} variation${
                    result.created.runnableVariants === 1 ? '' : 's'
                }${result.created.unavailableVariants ? ` (${result.created.unavailableVariants} skipped)` : ''}`,
                'success',
                true
            );

            void navigate(`/user/entryOptimizer/studies/${result.created.studyId}`);
        }
    });

    const parsedPilotEntityCount = Number(pilotEntityCount);
    const validPilotEntityCount = Number.isInteger(parsedPilotEntityCount) && parsedPilotEntityCount >= 5 && parsedPilotEntityCount <= 50;
    const canLaunch =
        strategyId !== null &&
        sourceVersionId !== null &&
        sourceExperimentId !== '' &&
        runnableVariants.length >= 2 &&
        preflight?.gridError == null &&
        validPilotEntityCount &&
        parsePositiveInt(fullTopK) !== null &&
        !createStudyMutation.isPending;

    const launch = () => {
        if (strategyId === null || sourceVersionId === null || sourceExperimentId === '' || !validPilotEntityCount) return;

        createStudyMutation.mutate({
            strategyId,
            sourceVersionId,
            sourceExperimentId: Number.parseInt(sourceExperimentId, 10),
            duration: '30m',
            horizonSeconds: 604800,
            ...(Object.keys(domainOverrides).length ? { domainOverrides } : {}),
            pilotEntityCount: parsedPilotEntityCount,
            ...(parsePositiveInt(fullTopK) !== null ? { fullTopK: parsePositiveInt(fullTopK)! } : {}),
            includeGateDiscovery
        });
    };

    return (
        <UserLayout containerSize="1100px" title="New Variation Search - Project 314">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-zinc-100">New Variation Search</h1>
                <p className="mt-1 text-sm text-zinc-400">
                    Test whole variations of an entry — for example different moving-average periods — not just its settings. We try each
                    variation on a small sample first, then run the most promising ones on the full set of entities. Each variation is a
                    real test run, so keep the list focused.
                </p>
            </div>

            <Card subtitle="We vary this strategy's live entry logic." title="1. Strategy">
                <TradingStrategySelect
                    value={strategyId}
                    onChange={(nextStrategyId) => {
                        setStrategyId(nextStrategyId);
                        // Axis ids hash level VALUES, so drafts from another
                        // strategy would silently apply (or break preflight).
                        setDomainDraft({});
                        setSourceExperimentId('');
                    }}
                />
                {strategy && sourceVersionId === null && (
                    <Callout className="mt-3" variant="warning">
                        This strategy has no entry logic attached yet. Attach one in the Trade Logic editor first.
                    </Callout>
                )}
            </Card>

            {sourceVersionId !== null && (
                <Card
                    className="mt-4"
                    subtitle="The levels this entry uses; list the alternatives you want to try for each."
                    title="2. Variations to test"
                >
                    {preflightQuery.isLoading ? (
                        <DataLoadingSpinner />
                    ) : preflightQuery.error instanceof Error ? (
                        <Callout variant="error">{preflightQuery.error.message}</Callout>
                    ) : preflight ? (
                        <>
                            {preflight.gridError && (
                                <Callout className="mb-3" variant="warning">
                                    {preflight.gridError} Trim the values below until it fits.
                                </Callout>
                            )}
                            <div className="flex flex-col gap-3">
                                {preflight.axes.map((axis) => (
                                    <div className="rounded-md border border-zinc-800 bg-black/20 p-3" key={axis.axisId}>
                                        <div className="text-sm font-medium text-zinc-100">{axis.label}</div>
                                        <div className="mt-0.5 text-xs text-zinc-500">
                                            Used in {axis.occurrences.length} place{axis.occurrences.length === 1 ? '' : 's'} in the entry;
                                            all of them change together.
                                        </div>
                                        <FormInput
                                            className="mt-2"
                                            helpText="Comma-separated levels (e.g. sma-10, sma-20, ema-50, vwap). Your current value is always included."
                                            label="Alternatives to try"
                                            value={domainDraft[axis.axisId] ?? axis.domain.join(', ')}
                                            onChange={(event) =>
                                                setDomainDraft((previous) => ({ ...previous, [axis.axisId]: event.target.value }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 text-sm text-zinc-300">
                                <span className="font-medium text-cyan-300">{runnableVariants.length}</span> variation
                                {runnableVariants.length === 1 ? '' : 's'} will be tested
                                {unavailableVariants.length > 0 && (
                                    <span className="text-zinc-500"> · {unavailableVariants.length} can&apos;t run</span>
                                )}
                            </div>

                            {unavailableVariants.length > 0 && (
                                <p className="mt-2 text-xs text-zinc-600">
                                    Skipped:{' '}
                                    {unavailableVariants
                                        .slice(0, 5)
                                        .map((variant) => `${variant.variantKey} (${variant.reasons[0] ?? 'unknown'})`)
                                        .join('; ')}
                                    {unavailableVariants.length > 5 ? '; …' : ''}
                                </p>
                            )}
                        </>
                    ) : null}
                </Card>
            )}

            {sourceVersionId !== null && (
                <Card className="mt-4" subtitle="Which entities to test on, and how aggressively to narrow down." title="3. Run settings">
                    <FormSelect
                        helpText="We reuse the entities and date window from one of your past experiments."
                        label="Test universe"
                        options={[{ label: 'Select an experiment…', value: '' }, ...experimentOptions]}
                        value={sourceExperimentId}
                        onChange={(event) => setSourceExperimentId(event.target.value)}
                    />
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <FormInput
                            helpText="Use 5–50 entities. The selected experiment must have at least 5 eligible entities for evaluation."
                            label="First-pass entities"
                            max={50}
                            min={5}
                            step={1}
                            type="number"
                            value={pilotEntityCount}
                            onChange={(event) => setPilotEntityCount(event.target.value)}
                        />
                        <FormInput
                            helpText="How many top variations advance to the full test."
                            label="Finalists"
                            value={fullTopK}
                            onChange={(event) => setFullTopK(event.target.value)}
                        />
                    </div>
                    <FormCheckbox
                        checked={includeGateDiscovery}
                        label="Also look for helpful new filters inside each variation"
                        onChange={(event) => setIncludeGateDiscovery(event.target.checked)}
                    />
                </Card>
            )}

            <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline-dark" onClick={() => navigate('/user/entryOptimizer')}>
                    Cancel
                </Button>
                <Button disabled={!canLaunch} onClick={launch}>
                    {createStudyMutation.isPending
                        ? 'Starting…'
                        : `Start Search (${runnableVariants.length} variation${runnableVariants.length === 1 ? '' : 's'})`}
                </Button>
            </div>
        </UserLayout>
    );
};

export default EntryOptimizerStudyNew;
